// src/hooks/useAuthEvents.js - Handle authentication events
import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

/**
 * Custom hook to handle authentication events
 * This listens to events dispatched by axios interceptors
 */
export const useAuthEvents = () => {
  const { logout } = useAuth();
  const { showError, showWarning } = useToast();

  useEffect(() => {
    // Handle unauthorized events (401 errors or expired tokens)
    const handleUnauthorized = (event) => {
      const message = event.detail?.message || 'Your session has expired. Please log in again.';
      showError(message);
      logout();
    };

    // Handle forbidden events (403 errors)
    const handleForbidden = (event) => {
      const message = event.detail?.message || 'You don\'t have permission to perform this action.';
      showWarning(message);
    };

    // Handle network errors
    const handleNetworkError = (event) => {
      const message = event.detail?.message || 'Network error. Please check your connection.';
      showError(message);
    };

    // Handle server errors
    const handleServerError = (event) => {
      const message = event.detail?.message || 'Server error. Please try again later.';
      showError(message);
    };

    // Add event listeners
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('auth:forbidden', handleForbidden);
    window.addEventListener('network:error', handleNetworkError);
    window.addEventListener('server:error', handleServerError);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('auth:forbidden', handleForbidden);
      window.removeEventListener('network:error', handleNetworkError);
      window.removeEventListener('server:error', handleServerError);
    };
  }, [logout, showError, showWarning]);
};

export default useAuthEvents;